import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { inferCategoryTemplate, relatedFirst, type OpeningHours } from "@majourneys/shared";
import { Icon } from "@majourneys/icons";
import { useDisplayPriceParts } from "../lib/currency";
import { unwrap } from "@majourneys/api-client";
import { useBookingContext } from "../provider";
import { activityHrefParts } from "../lib/activity-href";
import { ImageWithFallback } from "../internal-ui/image-with-fallback";
import { PW, pewterButton, pewterPanelApproved } from "../internal-ui/pewter-shell";
import { OpenNowPill } from "./restaurant/open-now-pill";

interface SimilarListingsActivity {
  slug: string;
  title: string;
  images: string[];
  price: number;
  typePrice?: number;
  rating?: number | string | null;
  mode?: "bookable" | "explore";
  segment?: string | null;
  template?: string | null;
  category: { id: string; name: string; slug: string } | null;
  city: { id: string; name: string; slug: string };
}

interface SimilarListingsProps {
  activity: SimilarListingsActivity;
  lang: string;
  /** SSR fallback rows (route-loader prefetch); client query result wins. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fallbackItems?: any[];
  /**
   * SSR rows for the same-city fallback, used when the activity is the only
   * one in its category in that city (e.g. the single quad tour in Casablanca).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fallbackCityItems?: any[];
}

interface ActivityFilters {
  q?: string;
  category?: string;
  city?: string;
  min_price?: string;
  max_price?: string;
  sort?: string;
  page?: string;
  page_size?: string;
  exact_category?: string;
  mode?: "bookable" | "explore" | "all";
  segment?: string;
  lang?: string;
  currency?: string;
}

export const similarKeys = {
  similar: (filters: ActivityFilters) =>
    ["activities", "similar", filters] as const,
};

/**
 * The card wears the frame, the section does not (owner, 2026-09-15): a panel
 * around a row of framed cards is a box inside a box.
 *
 * "#20 More things to do" from the pewter lab (owner pick): one rail of
 * framed cards at every width, arrows from `md`, "From" before every price.
 */
const CARD_RADIUS = 20;
const CARD_FRAME = pewterPanelApproved(CARD_RADIUS);

type SimilarItem = {
  id: string;
  slug: string;
  title: string;
  /** One line on what the place is ("Moroccan restaurant", "Italian cooking by…"). */
  subtitle?: string | null;
  openingHours?: OpeningHours | null;
  images: string[];
  price: number;
  typePrice?: number;
  rating?: number;
  mode?: "bookable" | "explore";
  segment?: string | null;
  template?: string | null;
  category: { id: string; name: string; slug: string } | null;
  city: { id: string; name: string; slug: string };
};

function isRestaurantTemplate(
  template: string | null,
  categorySlug: string | null,
  mode?: "bookable" | "explore",
  segment?: string | null,
): boolean {
  const t = inferCategoryTemplate(template, categorySlug, { mode: mode ?? null, segment: segment ?? null });
  return t === "restaurant" || t === "explore-restaurant";
}

/**
 * Whether a card may show a price. Explore items carry no price and no book
 * wording; restaurant tables are free to reserve, so a stored price there is
 * stale data, never something to show (owner rule). Exported for tests.
 */
export function showsPrice(item: Pick<SimilarItem, "price" | "mode" | "segment" | "template" | "category">): boolean {
  if (item.mode === "explore") return false;
  const template = inferCategoryTemplate(item.template ?? null, item.category?.slug ?? null, {
    mode: item.mode ?? null,
    segment: item.segment ?? null,
  });
  if (template === "restaurant" || template === "explore-restaurant") return false;
  return typeof item.price === "number" && item.price > 0;
}

export function SimilarListings({
  activity,
  lang,
  fallbackItems,
  fallbackCityItems,
}: SimilarListingsProps) {
  const { t } = useTranslation();
  const { api } = useBookingContext();
  const filters = buildSimilarFilters(activity, lang);
  const enabled = Boolean(filters);

  const { data: response, isSuccess } = useQuery({
    queryKey: similarKeys.similar(filters ?? { lang, page_size: "5" }),
    queryFn: async () =>
      unwrap(await api.activities.explore.search.get({ query: filters! })),
    enabled,
  });

  // fallbackItems: loader-provided rows so the section (the page's only
  // lateral venue links) renders during SSR — the streamed query data does
  // not resolve within the server render, leaving crawlers zero links.
  const sameCategory = pickItems(
    response?.data ?? fallbackItems,
    activity.slug,
  );

  // Same-category can legitimately come back empty: plenty of cities stock
  // exactly one activity per category (one quad tour in Casablanca, one jet
  // ski, …), which left those pages with no lateral links at all. Widen to
  // the rest of the city rather than render nothing.
  const cityFilters = buildSimilarCityFilters(activity, lang);
  const cityQueryEnabled =
    Boolean(cityFilters) && isSuccess && sameCategory.length === 0;

  const { data: cityResponse } = useQuery({
    queryKey: similarKeys.similar(cityFilters ?? { lang, page_size: "8" }),
    queryFn: async () =>
      unwrap(await api.activities.explore.search.get({ query: cityFilters! })),
    enabled: cityQueryEnabled,
  });

  const sameCity = pickItems(
    cityResponse?.data ?? fallbackCityItems,
    activity.slug,
    activity.category?.slug,
  );
  const usingCityFallback = sameCategory.length === 0;
  const items = usingCityFallback ? sameCity : sameCategory;

  if (!enabled || items.length === 0) return null;

  // A restaurant's row is restaurants: say so, rather than "Similar listings"
  // (which reads like a directory, and in fr/es/it/de/nl says "activities").
  const restaurantRow =
    !usingCityFallback && isRestaurantTemplate(activity.template ?? null, activity.category?.slug ?? null, activity.mode, activity.segment);

  return (
    <section className="mt-10" aria-labelledby="similar-listings-title">
      <SimilarRail
        items={items as SimilarItem[]}
        lang={lang}
        pageCity={activity.city.slug}
        header={
          <div className="min-w-0">
            <p className="font-sans text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: PW.muted }}>
              {usingCityFallback
                ? t("activity.more_in_city_kicker", {
                    defaultValue: "Keep exploring",
                  })
                : t("activity.similar_kicker", {
                    defaultValue: "You may also like",
                  })}
            </p>
            <h2
              id="similar-listings-title"
              className="mt-1 font-heading text-[22px] font-bold tracking-[-0.01em] md:text-[26px]"
              style={{ color: PW.navy }}
            >
              {usingCityFallback
                ? t("activity.more_in_city_title", {
                    city: activity.city.name,
                    defaultValue: `More things to do in ${activity.city.name}`,
                  })
                : restaurantRow
                  ? t("activity.more_restaurants_title", {
                      city: activity.city.name,
                      defaultValue: `More restaurants in ${activity.city.name}`,
                    })
                  : t("activity.similar_title", {
                      city: activity.city.name,
                      defaultValue: `Similar listings in ${activity.city.name}`,
                    })}
            </h2>
          </div>
        }
      />
    </section>
  );
}

/**
 * One sideways rail at every width. On a phone the next card is half in view,
 * which is what says there are more; from `md` two arrow controls scroll it
 * one card at a time, flipped in RTL where the rail runs the other way.
 *
 * The vertical padding is load-bearing: a scroller clips, and without room
 * above and below it cuts the frames' outer hairline and shadow.
 */
function SimilarRail({ items, lang, header, pageCity }: { items: SimilarItem[]; lang: string; header: ReactNode; pageCity: string }) {
  const { t } = useTranslation();
  const rail = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: true, end: true });

  const measure = useCallback(() => {
    const el = rail.current;
    if (!el) return;
    // scrollLeft is 0 at the start and negative towards the end in RTL.
    const travelled = Math.abs(el.scrollLeft);
    const room = el.scrollWidth - el.clientWidth;
    setEdges({ start: travelled <= 2, end: travelled >= room - 2 });
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, items.length]);

  const go = (direction: 1 | -1) => {
    const el = rail.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-similar-card]");
    const step = (card?.offsetWidth ?? 240) + 16;
    const rtl = getComputedStyle(el).direction === "rtl";
    el.scrollBy({ left: direction * step * (rtl ? -1 : 1), behavior: "smooth" });
  };

  const arrow = (direction: 1 | -1) => {
    const disabled = direction < 0 ? edges.start : edges.end;
    return (
      <button
        type="button"
        onClick={() => go(direction)}
        disabled={disabled}
        aria-label={direction < 0 ? t("common.previous") : t("common.next")}
        className="grid h-8 w-8 place-items-center rounded-full transition-opacity duration-150 disabled:cursor-default disabled:opacity-40"
        style={{ ...pewterButton, color: PW.navy }}
      >
        <Icon
          name={direction < 0 ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"}
          className="text-[18px] rtl:-scale-x-100"
          aria-hidden="true"
        />
      </button>
    );
  };

  const overflows = !(edges.start && edges.end);

  return (
    <>
      <div className="mb-2 flex items-end justify-between gap-3">
        {header}
        {overflows && (
          <div className="hidden shrink-0 gap-1.5 md:flex">
            {arrow(-1)}
            {arrow(1)}
          </div>
        )}
      </div>
      <div
        ref={rail}
        onScroll={measure}
        data-testid="similar-rail"
        className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 py-3 [scrollbar-width:none] md:-mx-1 md:scroll-px-1 md:px-1 [&::-webkit-scrollbar]:hidden"
      >
        {items.map((item) => (
          <div key={item.id} data-similar-card className="flex w-[220px] shrink-0 snap-start md:w-[240px]">
            <SimilarCard item={item} lang={lang} pageCity={pageCity} />
          </div>
        ))}
      </div>
    </>
  );
}

function SimilarCard({ item, lang, pageCity }: { item: SimilarItem; lang: string; pageCity: string }) {
  const { t } = useTranslation();
  const isExplore = item.mode === "explore";
  const display = useDisplayPriceParts();
  const linkParts = activityHrefParts({
    lang,
    citySlug: item.city?.slug ?? null,
    categorySlug: item.category?.slug ?? null,
    segment: isExplore ? (item.segment ?? null) : null,
    slug: item.slug,
  });
  const href = `/${linkParts.params.lang}/${linkParts.params.city}/${linkParts.params.category}/${linkParts.params.slug}`;
  const price = showsPrice(item) ? display(item.price) : null;
  // The heading already names the city, so every card saying "Marrakech" again
  // told the guest nothing (lab round 26, item 198). Say what the place is
  // instead, and for a restaurant whether it is open, on the venue's clock.
  const sameCity = item.city?.slug === pageCity;
  const subtitle = item.subtitle?.trim() || null;
  const detail = sameCity && subtitle ? subtitle : item.city.name;
  const restaurant = isRestaurantTemplate(item.template ?? null, item.category?.slug ?? null, item.mode, item.segment);

  return (
    <a
      href={href}
      className="group flex w-full flex-col p-1.5 no-underline transition-transform duration-200 ease-out hover:-translate-y-[2px]"
      style={CARD_FRAME}
    >
      {/* The photo clips itself; the frame never clips, or it would eat its
          own outer hairline. */}
      <div className="overflow-hidden rounded-[14px]">
        <ImageWithFallback
          src={item.images[0]}
          alt={item.title}
          className="h-[140px] w-full object-cover transition-transform duration-200 group-hover:scale-[1.03] md:h-[150px]"
          sizes="240px"
          srcsetWidths={[360, 480]}
        />
      </div>
      <div className="flex flex-1 flex-col px-1.5 pb-1 pt-2">
        {/* Names wrap: "L'Avenue Brasserie Nouvelle" was cut off with "…". */}
        <p className="font-sans text-[14px] font-semibold leading-tight" style={{ color: PW.navy }}>
          {item.title}
        </p>
        <p className="mt-0.5 font-sans text-[12px] leading-[16px]" style={{ color: PW.muted }}>
          {detail}
        </p>
        {/* Pinned to the card's foot: cards in the row stretch to the tallest
            one, so a short description left an empty band under the pill.
            Now the pills line up and the slack sits above them. */}
        {restaurant && item.openingHours && (
          <div className="mt-auto flex pt-1.5">
            <OpenNowPill hours={item.openingHours} wrap className="!py-1 !text-[12px]" />
          </div>
        )}
        {/* Was `{item.price.toLocaleString(lang)} MAD`: pinned to dirhams, so
            a guest browsing in USD read a USD price on the activity above and
            MAD prices in this row directly beneath it. */}
        {price && (
          <p data-testid="similar-price" className="mt-auto pt-1.5 font-sans text-[13px]" style={{ color: PW.navy }}>
            <span style={{ color: PW.muted }}>{t("activity.from")} </span>
            <b className="tabular-nums">
              {price.value} {price.currency}
            </b>
          </p>
        )}
      </div>
    </a>
  );
}

/**
 * Exported so route loaders can prefetch the same query into the QueryClient —
 * without the prefetch this component renders nothing during SSR (plain
 * useQuery) and crawlers see zero lateral venue links on activity pages.
 */
export function buildSimilarFilters(
  activity: Pick<
    SimilarListingsActivity,
    "city" | "category" | "mode" | "segment"
  >,
  lang: string,
): ActivityFilters | null {
  if (!activity.city?.slug) return null;

  if (activity.category?.slug) {
    return {
      city: activity.city.slug,
      category: activity.category.slug,
      exact_category: "true",
      page_size: "5",
      lang,
      // Search defaults to bookable rows; explore items that carry a category
      // (e.g. explore restaurants) got zero results without this — the section
      // has silently never rendered on those pages (found 2026-07-11).
      ...(activity.mode === "explore" ? { mode: "explore" as const } : {}),
    };
  }

  if (activity.mode === "explore" && activity.segment) {
    return {
      city: activity.city.slug,
      segment: activity.segment,
      mode: "explore",
      page_size: "5",
      lang,
    };
  }

  return null;
}

/**
 * Widest net: everything else published in the same city, ignoring category.
 * Used only when buildSimilarFilters' query comes back with nothing but the
 * current activity — otherwise single-activity categories render no section.
 * Exported so route loaders can prefetch it for SSR, same as the primary one.
 */
export function buildSimilarCityFilters(
  activity: Pick<SimilarListingsActivity, "city" | "mode">,
  lang: string,
): ActivityFilters | null {
  if (!activity.city?.slug) return null;

  return {
    city: activity.city.slug,
    // Wide enough to choose from: the rail ranks these by how close each
    // outing is to this one (relatedFirst), then keeps four.
    page_size: "24",
    lang,
    ...(activity.mode === "explore" ? { mode: "explore" as const } : {}),
  };
}

function pickItems(rows: unknown, currentSlug: string, currentCategory?: string | null) {
  if (!Array.isArray(rows)) return [];
  return relatedFirst(
    rows.filter((item: { slug: string }) => item.slug !== currentSlug),
    currentCategory ?? null,
  ).slice(0, 4);
}

